import jwt_decode, { JwtDecodeOptions } from 'jwt-decode';

import { UserLogged } from '@/services/User/types';

/**
 * Chave do token de perspectiva no `localStorage` — o token que um interno
 * usa para navegar como se fosse o parceiro (`?mode=view`).
 *
 * Mora aqui, e não no contexto que a escreve, porque quem lê essa chave
 * (`services/request.ts`, `pages/Partners/utils/geoseazoneUrl.ts`) não pode
 * depender de um módulo de contexto React. Chave duplicada em string solta é
 * divergência silenciosa: quem lê o valor errado não quebra, passa a agir em
 * nome da sessão errada.
 */
export const PERSPECTIVE_TOKEN_KEY = 'sapron-pms-perspective-token';

interface DecodedJwt {
  exp: any;
  token: string;
  options?: JwtDecodeOptions;
}

export function getToken() {
  const userData = localStorage.getItem('sapron-pms-user');
  if (userData) {
    const { access } = JSON.parse(userData);
    return access;
  }
  return null;
}

export function checkIfTokenExpired() {
  const token = getToken();

  if (token) {
    const decodeJwt: DecodedJwt = jwt_decode(token);
    const expiry = decodeJwt.exp;
    const now = new Date();
    return now.getTime() > expiry * 1000;
  }
  return false;
}

/** Tira as aspas que um link de SSO pode trazer em volta do valor. */
const clearParam = (param: string) => `${param}`.replace(/^"|"$/g, '');

/**
 * A sessão do sapron a partir de um JWT de SSO.
 *
 * É o mecanismo que o `UserContext` já usa no fluxo `#token=` (a wallet
 * mandando o parceiro para cá), extraído para cá porque agora tem um segundo
 * chamador — o handshake do iframe do Programa de Indicação. Mora ao lado de
 * `getToken` pelo motivo do comentário de `PERSPECTIVE_TOKEN_KEY`: quem precisa
 * da forma da sessão não pode depender de um módulo de contexto React, e duas
 * cópias dessa forma divergiriam em silêncio.
 *
 * `refresh` recebe o access token, como sempre recebeu neste fluxo. É dívida
 * conhecida e mapeada; trocar isso aqui mudaria o fluxo existente sem que nada
 * nesta entrega peça.
 */
export function ssoUserFromToken(token: string): UserLogged {
  const decoded: { email: string } = jwt_decode(token);
  return { email: decoded.email, access: clearParam(token), refresh: clearParam(token) };
}

/**
 * Se o `token` de um repasse (`#token=` / `?token=`) e um JWT que da para usar.
 *
 * A Wallet manda `#token=undefined&target=partner` quando o parceiro entrou
 * nela pelo IDP e nao tem cookie legado. Tratar a string `"undefined"` como JWT
 * derrubava a tela inteira: o `jwt_decode` lanca no render do `RequireAuth` e o
 * SPA fica em branco.
 */
export function tokenDeRepasseUtilizavel(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    jwt_decode(clearParam(token));
    return true;
  } catch {
    return false;
  }
}
